import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Empty, Row, Select, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import salesOrderService, { type SalesOrderCostingReport } from '@/services/salesOrder.service';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { formatDate, formatMoney, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

const { Title, Text } = Typography;

function amt(v: unknown): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function pct(v: number | null | undefined): string {
  return v == null ? '—' : formatPercent(v);
}

function dateText(v: string | null | undefined): string {
  return v ? formatDate(v) || '—' : '—';
}

function marginColor(v: number | null | undefined): string | undefined {
  if (v == null) return undefined;
  return v < 0 ? '#cf1322' : '#389e0d';
}

const moneyCol = <T,>(title: string, dataIndex: keyof T & string) => ({
  title,
  dataIndex,
  align: 'right' as const,
  render: (v: number | null) => (v == null ? '—' : amt(v)),
});

const ReportBody: React.FC<{ report: SalesOrderCostingReport; printMode?: boolean }> = ({ report, printMode }) => {
  const { header, sell, estimate, actual, committed, margin } = report;
  const ccy = header.currency;
  const shared = header.attribution_share != null && header.attribution_share < 1;
  const estimateHasSelling = estimate.lines.some(
    (l) => l.estimate_selling != null && Math.abs(l.estimate_selling - l.estimate_cost) > 0.009
  );
  const tableProps = { size: 'small' as const, pagination: false as const, bordered: true };

  const summaryRows = [
    { key: 'revenue', label: `Order value (excl. VAT, ${ccy})`, estimate: margin.revenue, actual: margin.revenue, projected: margin.revenue },
    { key: 'cost', label: 'Cost', estimate: margin.estimated_cost, actual: margin.actual_cost, projected: margin.projected_cost },
    { key: 'margin', label: 'Margin', estimate: margin.estimated_margin, actual: margin.actual_margin, projected: margin.projected_margin },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Title level={4} style={{ margin: 0 }}>
          Project Costing Report — {header.sales_order_number}
        </Title>
        <Text type="secondary">Generated {formatDate(new Date().toISOString()) || ''}</Text>
      </div>

      <Descriptions size="small" bordered column={printMode ? 3 : { xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label="Customer order">{header.sales_order_number}</Descriptions.Item>
        <Descriptions.Item label="Order date">{dateText(header.order_date)}</Descriptions.Item>
        <Descriptions.Item label="Status">{header.order_status || '—'}</Descriptions.Item>
        <Descriptions.Item label="Customer">{header.customer_name || '—'}</Descriptions.Item>
        <Descriptions.Item label="Project">
          {header.project_number ? `${header.project_number} — ${header.project_name || ''}` : 'Not linked'}
        </Descriptions.Item>
        <Descriptions.Item label="Quotation">
          {header.quotation_number
            ? `${header.quotation_number}${header.quotation_revision != null ? ` (Rev ${header.quotation_revision})` : ''}`
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Estimate source">{estimate.source_label || '—'}</Descriptions.Item>
        <Descriptions.Item label="Currency">{ccy}</Descriptions.Item>
        <Descriptions.Item label="Cost attribution">
          {header.attribution_share == null
            ? '—'
            : shared
              ? `${formatPercent(header.attribution_share * 100)} of project (revenue share)`
              : '100% (only order on project)'}
        </Descriptions.Item>
      </Descriptions>

      {shared ? (
        <Alert
          type="info"
          showIcon
          message="Project costs are shared with other customer orders"
          description={`Actual and committed totals are this order's ${formatPercent(
            (header.attribution_share ?? 0) * 100
          )} revenue share of the project. Detail tables below list the full project transactions.`}
        />
      ) : null}

      <Card size="small" title="1. Margin summary">
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={6}>
            <Statistic title="Estimated margin" value={pct(margin.estimated_margin_pct)} valueStyle={{ color: marginColor(margin.estimated_margin) }} />
          </Col>
          <Col span={6}>
            <Statistic title="Actual margin (to date)" value={pct(margin.actual_margin_pct)} valueStyle={{ color: marginColor(margin.actual_margin) }} />
          </Col>
          <Col span={6}>
            <Statistic title="Projected margin (actual + open POs)" value={pct(margin.projected_margin_pct)} valueStyle={{ color: marginColor(margin.projected_margin) }} />
          </Col>
          <Col span={6}>
            <Statistic
              title="Cost variance vs estimate"
              value={`${amt(margin.cost_variance)} (${pct(margin.cost_variance_pct)})`}
              valueStyle={{ color: margin.cost_variance > 0 ? '#cf1322' : '#389e0d', fontSize: 18 }}
            />
          </Col>
        </Row>
        <Table
          {...tableProps}
          rowKey="key"
          dataSource={summaryRows}
          columns={[
            { title: '', dataIndex: 'label' },
            moneyCol<(typeof summaryRows)[number]>('Estimate', 'estimate'),
            moneyCol<(typeof summaryRows)[number]>('Actual to date', 'actual'),
            moneyCol<(typeof summaryRows)[number]>('Projected', 'projected'),
          ]}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>Margin %</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <strong>{pct(margin.estimated_margin_pct)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong>{pct(margin.actual_margin_pct)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong>{pct(margin.projected_margin_pct)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Margins are on order value excluding VAT. Projected cost = actual cost to date + open (unreceived) PO value.
        </Text>
      </Card>

      <Card size="small" title="2. Sell side">
        <Descriptions size="small" bordered column={printMode ? 4 : { xs: 1, sm: 2, lg: 4 }}>
          <Descriptions.Item label="Quotation value (excl. VAT)">{sell.quotation_value == null ? '—' : amt(sell.quotation_value)}</Descriptions.Item>
          <Descriptions.Item label="Order value (excl. VAT)">{amt(sell.order_value_excl_vat)}</Descriptions.Item>
          <Descriptions.Item label="VAT">{amt(sell.vat_amount)}</Descriptions.Item>
          <Descriptions.Item label="Order value (incl. VAT)">{amt(sell.order_value_incl_vat)}</Descriptions.Item>
          <Descriptions.Item label="Invoiced">{amt(sell.invoiced)}</Descriptions.Item>
          <Descriptions.Item label="Collected">{amt(sell.collected)}</Descriptions.Item>
          <Descriptions.Item label="Outstanding (AR)">{amt(sell.outstanding)}</Descriptions.Item>
          <Descriptions.Item label="Not yet invoiced">{amt(sell.uninvoiced)}</Descriptions.Item>
        </Descriptions>
        <Table
          {...tableProps}
          style={{ marginTop: 12 }}
          rowKey="id"
          dataSource={sell.invoices}
          locale={{ emptyText: 'No invoices yet' }}
          columns={[
            { title: 'Invoice', dataIndex: 'invoice_number', render: (v: string, r) => (r.is_shared ? `${v} (shared)` : v) },
            { title: 'Date', dataIndex: 'invoice_date', render: dateText },
            { title: 'Type', dataIndex: 'invoice_type' },
            { title: 'Status', dataIndex: 'invoice_status' },
            { title: 'Payment', dataIndex: 'payment_status' },
            moneyCol('Billed', 'billed'),
            moneyCol('Collected', 'collected'),
            moneyCol('Outstanding', 'outstanding'),
          ]}
        />
      </Card>

      <Card size="small" title={`3. Estimated cost — ${estimate.source_label || 'no estimate linked'}`}>
        {estimate.source_type ? (
          <>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Table
                  {...tableProps}
                  rowKey="key"
                  dataSource={estimate.breakdown}
                  columns={[{ title: 'Cost element', dataIndex: 'label' }, moneyCol('Estimated', 'amount')]}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Total estimated cost</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>{amt(estimate.cost_total)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Col>
              <Col xs={24} md={16}>
                <Descriptions size="small" bordered column={2}>
                  <Descriptions.Item label="Estimated cost">{amt(estimate.cost_total)}</Descriptions.Item>
                  <Descriptions.Item label="Estimated hours">{estimate.hours_total ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Estimated margin">{amt(estimate.margin)}</Descriptions.Item>
                  <Descriptions.Item label="Estimated margin %">{pct(estimate.margin_pct)}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
            <Table
              {...tableProps}
              style={{ marginTop: 12 }}
              rowKey="id"
              dataSource={estimate.lines}
              columns={[
                { title: '#', dataIndex: 'line_number', width: 50 },
                { title: 'Description', dataIndex: 'description' },
                { title: 'Qty', dataIndex: 'quantity', align: 'right' as const },
                moneyCol('Estimated cost', 'estimate_cost'),
                ...(estimateHasSelling
                  ? [
                      moneyCol<SalesOrderCostingReport['estimate']['lines'][number]>('Estimated selling', 'estimate_selling'),
                      moneyCol<SalesOrderCostingReport['estimate']['lines'][number]>('Margin', 'margin'),
                      { title: 'Margin %', dataIndex: 'margin_pct', align: 'right' as const, render: (v: number | null) => pct(v) },
                    ]
                  : []),
                { title: 'Est. hours', dataIndex: 'estimate_hours', align: 'right' as const, render: (v: number | null) => v ?? '—' },
                { title: 'Actual hours', dataIndex: 'actual_hours', align: 'right' as const, render: (v: number | null) => v ?? '—' },
              ]}
            />
          </>
        ) : (
          <Empty description="This order has no linked estimate (TRE / BOQ / estimation)." />
        )}
      </Card>

      <Card size="small" title="4. Actual cost">
        <Alert type={actual.cost_total ? 'success' : 'warning'} showIcon message={`Basis: ${actual.basis}`} style={{ marginBottom: 12 }} />
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Table
              {...tableProps}
              rowKey="category"
              dataSource={actual.by_category}
              locale={{ emptyText: 'No costs recorded yet' }}
              columns={[
                { title: 'Category', dataIndex: 'category' },
                moneyCol('Project total', 'project_total'),
                moneyCol('This order', 'attributed'),
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Total actual cost</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{amt(actual.project_cost_total)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{amt(actual.cost_total)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            {actual.procurement ? (
              <Descriptions size="small" bordered column={1} title="Procurement roll-up (project)">
                <Descriptions.Item label="PO ordered">{amt(actual.procurement.po_ordered)}</Descriptions.Item>
                <Descriptions.Item label="Goods received">{amt(actual.procurement.grn_received)}</Descriptions.Item>
                <Descriptions.Item label="Supplier invoiced">{amt(actual.procurement.supplier_invoiced)}</Descriptions.Item>
                <Descriptions.Item label="Supplier paid">{amt(actual.procurement.supplier_paid)}</Descriptions.Item>
              </Descriptions>
            ) : null}
          </Col>
        </Row>

        <Title level={5} style={{ marginTop: 16 }}>
          Posted project cost entries {actual.transactions_truncated ? '(latest 500)' : ''}
        </Title>
        <Table
          {...tableProps}
          rowKey="id"
          dataSource={actual.transactions}
          locale={{ emptyText: 'No cost entries posted to the project' }}
          columns={[
            { title: 'Date', dataIndex: 'cost_date', render: dateText, width: 100 },
            { title: 'Category', dataIndex: 'cost_category' },
            { title: 'Source', dataIndex: 'source_type' },
            { title: 'Document', dataIndex: 'source_document_number' },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Qty', dataIndex: 'quantity', align: 'right' as const },
            moneyCol('Unit cost', 'unit_cost'),
            moneyCol('Amount', 'total_cost'),
          ]}
        />

        <Title level={5} style={{ marginTop: 16 }}>
          Goods received against project POs
        </Title>
        <Table
          {...tableProps}
          rowKey={(r) => `${r.grn_id}-${r.item_code}-${r.item_description}`}
          dataSource={actual.goods_received}
          locale={{ emptyText: 'No goods received yet' }}
          columns={[
            { title: 'GRN', dataIndex: 'grn_number' },
            { title: 'Date', dataIndex: 'grn_date', render: dateText, width: 100 },
            { title: 'PO', dataIndex: 'po_number' },
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'Item', dataIndex: 'item_description', render: (v: string, r) => [r.item_code, v].filter(Boolean).join(' — ') },
            { title: 'Qty', dataIndex: 'quantity', align: 'right' as const },
            moneyCol('Unit price', 'unit_price'),
            moneyCol('Value', 'value'),
          ]}
        />
      </Card>

      <Card size="small" title="5. Committed cost — open purchase orders">
        <Descriptions size="small" bordered column={printMode ? 2 : { xs: 1, sm: 2 }} style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Open PO value (project)">{amt(committed.open_po_value_project)}</Descriptions.Item>
          <Descriptions.Item label="Open PO value (this order)">{amt(committed.open_po_value_attributed)}</Descriptions.Item>
        </Descriptions>
        <Table
          {...tableProps}
          rowKey={(r) => `${r.po_id}-${r.line_number}`}
          dataSource={committed.lines}
          locale={{ emptyText: 'No open purchase order lines' }}
          columns={[
            { title: 'PO', dataIndex: 'po_number' },
            { title: 'Date', dataIndex: 'po_date', render: dateText, width: 100 },
            { title: 'Status', dataIndex: 'po_status' },
            { title: 'Supplier', dataIndex: 'supplier_name' },
            { title: 'Item', dataIndex: 'item_description', render: (v: string, r) => [r.item_code, v].filter(Boolean).join(' — ') },
            { title: 'Ordered', dataIndex: 'ordered_quantity', align: 'right' as const },
            { title: 'Received', dataIndex: 'received_quantity', align: 'right' as const },
            { title: 'Open', dataIndex: 'open_quantity', align: 'right' as const, render: (v: number, r) => `${v} ${r.uom || ''}`.trim() },
            moneyCol('Unit price', 'unit_price'),
            moneyCol('Open value', 'open_value'),
          ]}
        />
      </Card>
    </div>
  );
};

const SalesProjectCostingReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const salesOrderId = searchParams.get('so') || '';
  const [search, setSearch] = useState('');
  const [printing, setPrinting] = useState(false);

  const { data: orders, isFetching: searching } = useQuery({
    queryKey: ['project-costing-so-search', search],
    queryFn: () => salesOrderService.getAll(search ? { search } : undefined),
    staleTime: 60_000,
  });

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['sales-project-costing', salesOrderId],
    queryFn: () => salesOrderService.getProjectCostingReport(salesOrderId),
    enabled: Boolean(salesOrderId),
  });

  const options = useMemo(() => {
    const list = (orders ?? []).slice(0, 50).map((o) => ({
      value: o.id as string,
      label: `${o.sales_order_number} — ${o.customer_name ?? ''}`,
    }));
    if (report && !list.some((o) => o.value === report.header.sales_order_id)) {
      list.unshift({
        value: report.header.sales_order_id,
        label: `${report.header.sales_order_number} — ${report.header.customer_name ?? ''}`,
      });
    }
    return list;
  }, [orders, report]);

  if (printing && report) {
    return (
      <PrintPreviewChrome
        title={`Project costing — ${report.header.sales_order_number}`}
        fileName={`${report.header.sales_order_number}-project-costing`}
        onBack={() => setPrinting(false)}
      >
        <div className="print-doc-a4 print-doc-a4--dimensional-landscape">
          <div style={{ padding: '10mm 12mm' }}>
            <ReportBody report={report} printMode />
          </div>
        </div>
      </PrintPreviewChrome>
    );
  }

  const errorMessage =
    (error as { response?: { data?: { message?: string } }; message?: string } | null)?.response?.data?.message ||
    (error as { message?: string } | null)?.message;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space wrap>
          <Title level={3} style={{ margin: 0 }}>
            Project Costing Report
          </Title>
          <Select
            showSearch
            allowClear
            style={{ minWidth: 380 }}
            placeholder="Search customer order number or customer"
            value={salesOrderId || undefined}
            filterOption={false}
            onSearch={setSearch}
            notFoundContent={searching ? <Spin size="small" /> : 'No customer orders'}
            options={options}
            onChange={(value?: string) => {
              const params = new URLSearchParams(searchParams);
              if (value) params.set('so', value);
              else params.delete('so');
              setSearchParams(params, { replace: true });
            }}
          />
        </Space>
        <Space>
          {report ? (
            <Button onClick={() => navigate(`/growth/orders/${report.header.sales_order_id}`)}>Open customer order</Button>
          ) : null}
          <Button type="primary" icon={<PrinterOutlined />} disabled={!report} onClick={() => setPrinting(true)}>
            Print
          </Button>
        </Space>
      </Space>

      {!salesOrderId ? (
        <Card>
          <Empty description="Select a customer order to see its full project costing: sell side, estimate, actual cost, open commitments and margin." />
        </Card>
      ) : isLoading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message="Could not load the costing report" description={errorMessage} />
      ) : report ? (
        <ReportBody report={report} />
      ) : (
        <Alert type="warning" showIcon message="Customer order not found" />
      )}

      {report && !report.header.project_id ? (
        <Tag color="orange">This customer order is not linked to a project, so no actual or committed costs can be traced.</Tag>
      ) : null}
    </div>
  );
};

export default SalesProjectCostingReportPage;
